import * as React from "react"
import { CalendarIcon } from "lucide-react"
import { format } from "date-fns"

import { Button } from "./Button"
import { Calendar } from "./calendar"
import { Input } from "./input"
import { Label } from "./label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"
import { cn } from "../../lib/utils"

function formatDate(date) {
  if (!date) {
    return ""
  }

  return format(date, "PPP")
}

function isValidDate(date) {
  if (!date) {
    return false
  }
  return !isNaN(date.getTime())
}

/**
 * Single Date Picker component using shadcn/ui
 * @param {Object} props - Component props
 * @param {Date} props.value - Selected date value
 * @param {Function} props.onChange - Callback when date changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.label - Label for the date picker
 * @param {boolean} props.disabled - Whether the picker is disabled
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} DatePicker component
 */
export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  label,
  disabled = false,
  className,
  ...props
}) {
  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState(value)
  const [inputValue, setInputValue] = React.useState(formatDate(value))

  React.useEffect(() => {
    setDate(value)
    setInputValue(formatDate(value))
  }, [value])

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate)
    setInputValue(formatDate(selectedDate))
    setOpen(false)
    if (onChange) {
      onChange(selectedDate)
    }
  }

  const handleInputChange = (e) => {
    const inputVal = e.target.value
    setInputValue(inputVal)
    
    // Try to parse the input as a date
    const parsedDate = new Date(inputVal)
    if (isValidDate(parsedDate)) {
      setDate(parsedDate)
      if (onChange) {
        onChange(parsedDate)
      }
    }
  }

  return (
    <div className={cn("flex flex-col gap-2", className)} {...props}>
      {label && (
        <Label htmlFor="date" className="text-sm font-medium text-gray-700">
          {label}
        </Label>
      )}
      <div className="relative flex gap-2">
        <Input
          id="date"
          value={inputValue}
          placeholder={placeholder}
          className="bg-background pr-10"
          disabled={disabled}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault()
              setOpen(true)
            }
          }}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-1/2 right-2 h-6 w-6 -translate-y-1/2 p-0"
              disabled={disabled}
            >
              <CalendarIcon className="h-4 w-4" />
              <span className="sr-only">Open calendar</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            align="start"
            sideOffset={4}
          >
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateSelect}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}

/**
 * Date Range Picker component using shadcn/ui
 * @param {Object} props - Component props
 * @param {Object} props.value - Selected date range {from: Date, to: Date}
 * @param {Function} props.onChange - Callback when date range changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.label - Label for the date picker
 * @param {boolean} props.disabled - Whether the picker is disabled
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} DateRangePicker component
 */
export function DateRangePicker({
  value,
  onChange,
  placeholder = "Pick a date range",
  label,
  disabled = false,
  className,
  ...props
}) {
  const [open, setOpen] = React.useState(false)
  const [dateRange, setDateRange] = React.useState(value)

  React.useEffect(() => {
    setDateRange(value)
  }, [value])

  const handleDateRangeSelect = (selectedRange) => {
    // Always update local state for visual feedback
    setDateRange(selectedRange)

    // Detect first click (start of selection): previous had no from, or
    // DayPicker supplies from==to on first click. In both cases, don't emit onChange yet.
    const isInitialStart = (!dateRange?.from && selectedRange?.from && (!selectedRange?.to || (selectedRange.to && selectedRange.from && selectedRange.to.getTime() === selectedRange.from.getTime())))

    if (isInitialStart) {
      // Keep popover open and wait for the end date
      return
    }

    // Emit only when both dates are chosen, or when cleared (both undefined)
    const hasBoth = Boolean(selectedRange?.from) && Boolean(selectedRange?.to)
    const isCleared = !selectedRange?.from && !selectedRange?.to

    if (hasBoth || isCleared) {
      if (onChange) {
        onChange(selectedRange)
      }
      if (hasBoth) {
        setOpen(false)
      }
    }
  }

  const formatDateRange = (range) => {
    if (!range?.from) {
      return ""
    }
    if (!range.to) {
      return format(range.from, "dd/MM/yy")
    }
    return `${format(range.from, "dd/MM/yy")} - ${format(range.to, "dd/MM/yy")}`
  }

  return (
    <div className={cn("flex flex-col gap-2", className)} {...props}>
      {label && (
        <Label htmlFor="date-range" className="text-sm font-medium text-gray-700">
          {label}
        </Label>
      )}
      <div className="relative">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id="date-range"
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !dateRange && "text-muted-foreground"
              )}
              disabled={disabled}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              <span className="truncate max-w-[140px] inline-block">
                {formatDateRange(dateRange) || placeholder}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              defaultMonth={dateRange?.from}
              selected={dateRange}
              onSelect={handleDateRangeSelect}
              numberOfMonths={2}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}

export { DatePicker as default }
